import { NEUTRAL_EQ, DEFAULT_STEM_VOLUME } from "./state.js";
import { isMusical } from "../events/all.js";
function mapSection(state, sectionId, fn) {
    return state.sections.map((s) => (s.id === sectionId ? fn(s) : s));
}
function ensureStem(stems, name) {
    return stems[name] ?? { muted: false, volume: DEFAULT_STEM_VOLUME, eq: { ...NEUTRAL_EQ } };
}
/** The active DNA reference is the (single) `primary` — the derived scalar. */
function derivePrimary(refs) {
    return refs.find((r) => r.role === "primary") ?? null;
}
/** Demotes every existing `primary` reference to `secondary` — only one
 * reference is ever primary, and the newest upload/mashup wins (spec §9.3). */
function demoteExistingPrimaries(refs) {
    return refs.map((r) => (r.role === "primary" ? { ...r, role: "secondary" } : r));
}
export function reduce(state, event) {
    switch (event.type) {
        case "SectionAdded": {
            const newSection = {
                id: event.payload.id,
                name: event.payload.name,
                role: event.payload.role,
                // order is derived from arrival position, not the payload: events
                // replay in globalSeq order, so this is deterministic and unique even
                // for legacy events upcasted with a placeholder order. The payload's
                // `order` is informative only.
                order: state.sections.length,
                prompt: event.payload.prompt,
                audio: null,
                variations: [],
                stems: {},
                envelope: null,
                genreShift: null,
            };
            return {
                ...state,
                sections: [...state.sections, newSection],
                lastEventId: event.id,
            };
        }
        case "SectionPromptChanged": {
            const sections = mapSection(state, event.payload.sectionId, (s) => ({
                ...s,
                prompt: event.payload.newPrompt,
            }));
            return { ...state, sections, lastEventId: event.id };
        }
        case "SectionAudioBound": {
            const sections = mapSection(state, event.payload.sectionId, (s) => ({
                ...s,
                audio: {
                    assetHash: event.payload.assetHash,
                    startMs: event.payload.startMs,
                    endMs: event.payload.endMs,
                    history: [],
                },
            }));
            return { ...state, sections, lastEventId: event.id };
        }
        case "SectionAudioRebound": {
            const sections = mapSection(state, event.payload.sectionId, (s) => {
                if (s.audio === null)
                    return s;
                return {
                    ...s,
                    audio: {
                        assetHash: event.payload.newAssetHash,
                        startMs: event.payload.startMs,
                        endMs: event.payload.endMs,
                        history: [
                            ...s.audio.history,
                            { assetHash: s.audio.assetHash, startMs: s.audio.startMs, endMs: s.audio.endMs },
                        ],
                    },
                };
            });
            return { ...state, sections, lastEventId: event.id };
        }
        case "SectionVariationsGenerated": {
            // Merges a batch of alternative-take candidates into `section.variations`.
            // Dedupe by `variationId` so a batch the worker re-emits under retry does
            // not duplicate candidates (idempotent). New candidates enter with
            // `promoted: false`. Does NOT touch `section.audio` — generating
            // variations never changes the active clip.
            const sections = mapSection(state, event.payload.sectionId, (s) => {
                const known = new Set(s.variations.map((v) => v.variationId));
                const added = event.payload.variations
                    .filter((v) => !known.has(v.variationId))
                    .map((v) => ({
                    variationId: v.variationId,
                    batchId: event.payload.batchId,
                    assetHash: v.assetHash,
                    durationMs: v.durationMs,
                    label: v.label,
                    promoted: false,
                }));
                return added.length === 0 ? s : { ...s, variations: [...s.variations, ...added] };
            });
            return { ...state, sections, lastEventId: event.id };
        }
        case "SectionVariationPromoted": {
            // Promotes a candidate to be the section's active clip. Mirrors the
            // `SectionAudioRebound` mechanics: swap `audio` and push the prior binding
            // into `audio.history`. If the promoted `variationId` is not (yet) in
            // `section.variations`, the reducer leaves the state untouched — fail-soft,
            // never throws (consistent with the rest of this file).
            //
            // The promoted variation is a DIFFERENT clip, so the section's `stems`
            // (separated from the OLD clip) and `envelope` (the OLD clip's energy)
            // are now stale — they describe an audio asset no longer playing. Reset
            // them to their initial values (`{}` / `null`); a fresh stem-separation
            // and envelope-extraction job will re-materialize them for the new clip.
            const sections = mapSection(state, event.payload.sectionId, (s) => {
                if (!s.variations.some((v) => v.variationId === event.payload.variationId)) {
                    return s;
                }
                const priorHistory = s.audio?.history ?? [];
                const priorBinding = s.audio === null
                    ? []
                    : [
                        {
                            assetHash: s.audio.assetHash,
                            startMs: s.audio.startMs,
                            endMs: s.audio.endMs,
                        },
                    ];
                return {
                    ...s,
                    audio: {
                        assetHash: event.payload.newAssetHash,
                        startMs: 0,
                        endMs: event.payload.durationMs,
                        history: [...priorHistory, ...priorBinding],
                    },
                    // Stems/envelope belong to the previous clip — clear them.
                    stems: {},
                    envelope: null,
                    variations: s.variations.map((v) => ({
                        ...v,
                        promoted: v.variationId === event.payload.variationId,
                    })),
                };
            });
            return { ...state, sections, lastEventId: event.id };
        }
        case "StemMuted": {
            const sections = mapSection(state, event.payload.sectionId, (s) => {
                const current = ensureStem(s.stems, event.payload.stem);
                return {
                    ...s,
                    stems: { ...s.stems, [event.payload.stem]: { ...current, muted: true } },
                };
            });
            return { ...state, sections, lastEventId: event.id };
        }
        case "StemUnmuted": {
            const sections = mapSection(state, event.payload.sectionId, (s) => {
                if (!(event.payload.stem in s.stems))
                    return s;
                const current = s.stems[event.payload.stem];
                return {
                    ...s,
                    stems: { ...s.stems, [event.payload.stem]: { ...current, muted: false } },
                };
            });
            return { ...state, sections, lastEventId: event.id };
        }
        case "StemVolumeChanged": {
            const sections = mapSection(state, event.payload.sectionId, (s) => {
                const current = ensureStem(s.stems, event.payload.stem);
                return {
                    ...s,
                    stems: {
                        ...s.stems,
                        [event.payload.stem]: { ...current, volume: event.payload.volume },
                    },
                };
            });
            return { ...state, sections, lastEventId: event.id };
        }
        case "SectionStemsBound": {
            // Materializes the Demucs separation result onto the section. For each
            // stem in the payload we MERGE into `section.stems`: a stem the user
            // already touched (StemMuted/StemVolumeChanged ran before separation
            // finished) keeps its `muted`/`volume`/`eq`, only gaining `assetHash`;
            // a brand-new stem is created with defaults. Re-separation simply
            // overwrites `assetHash` with the new value — a new event superseding,
            // never a delete (invariant #1).
            const sections = mapSection(state, event.payload.sectionId, (s) => {
                const stems = { ...s.stems };
                for (const [name, assetHash] of Object.entries(event.payload.stems)) {
                    stems[name] = { ...ensureStem(s.stems, name), assetHash };
                }
                return { ...s, stems };
            });
            return { ...state, sections, lastEventId: event.id };
        }
        case "SectionEnvelopeExtracted": {
            // Materializes the envelope-extraction result onto the section. The whole
            // envelope is replaced: re-extraction simply overwrites `section.envelope`
            // with the new payload — a new event superseding, never a delete
            // (invariant #1). An unknown sectionId is a no-op (mapSection finds no
            // match), consistent with `SectionStemsBound`.
            const sections = mapSection(state, event.payload.sectionId, (s) => ({
                ...s,
                envelope: {
                    sampleRateHz: event.payload.sampleRateHz,
                    stems: event.payload.stems,
                },
            }));
            return { ...state, sections, lastEventId: event.id };
        }
        case "MasterEqAdjusted": {
            return {
                ...state,
                masterEq: { ...event.payload },
                lastEventId: event.id,
            };
        }
        case "DNAReferenceAttached": {
            // Legacy path (spec §4.6): KEPT and still emitted. The event has no
            // referenceId, so we synthesize one from `event.id`. It becomes the new
            // `source:"legacy"` primary, demoting any prior primary. A project whose
            // log has only `DNAReferenceAttached` materializes `dnaReferences` with
            // one item and a `dnaReference` derived field identical to pre-feature.
            //
            // Legacy "attach" semantics REPLACE: a project that emits
            // `DNAReferenceAttached` more than once should carry a single legacy
            // reference, not an accumulating pile of stale `secondary` cards. If a
            // `source:"legacy"` reference already exists, overwrite it in place;
            // otherwise append.
            const legacyRef = {
                id: `legacy:${event.id}`,
                bpm: event.payload.bpm,
                key: event.payload.key,
                mood: event.payload.mood,
                label: "DNA reference",
                source: "legacy",
                role: "primary",
                attachedAt: event.payload.attachedAt,
            };
            const existingLegacyIdx = state.dnaReferences.findIndex((r) => r.source === "legacy");
            const dnaReferences = existingLegacyIdx === -1
                ? [...demoteExistingPrimaries(state.dnaReferences), legacyRef]
                : demoteExistingPrimaries(state.dnaReferences).map((r, i) => i === existingLegacyIdx ? legacyRef : r);
            return {
                ...state,
                dnaReferences,
                dnaReference: derivePrimary(dnaReferences),
                lastEventId: event.id,
            };
        }
        case "ReferenceUploaded": {
            // Pushes a DNAReference (spec §4.6). `source:"mashup"` carries a ready
            // `dna`; `source:"upload"` enters with a placeholder DNA (bpm:0, key:"?",
            // mood:"analyzing") completed later by `ReferenceAnalyzed`. A new
            // `primary` demotes any prior primary — only one primary at a time.
            const newRef = {
                id: event.payload.referenceId,
                bpm: event.payload.dna?.bpm ?? 0,
                key: event.payload.dna?.key ?? "?",
                mood: event.payload.dna?.mood ?? "analyzing",
                label: event.payload.label,
                source: event.payload.source,
                role: event.payload.role,
                attachedAt: event.payload.uploadedAt,
            };
            const base = event.payload.role === "primary"
                ? demoteExistingPrimaries(state.dnaReferences)
                : state.dnaReferences;
            const dnaReferences = [...base, newRef];
            return {
                ...state,
                dnaReferences,
                dnaReference: derivePrimary(dnaReferences),
                lastEventId: event.id,
            };
        }
        case "ReferenceAnalyzed": {
            // Fills bpm/key/mood on the matching reference (spec §4.6). An orphan
            // `referenceId` (no matching upload) is ignored defensively — no entry is
            // created — but `lastEventId` still advances.
            //
            // Promotion-on-arrival (#6): if the project currently has NO `primary`
            // — every previous primary was removed via `ReferenceRemoved` and the
            // only remaining references are placeholders waiting on analysis — the
            // newly-analyzed reference is promoted to `primary`. The `ReferenceRemoved`
            // case deliberately leaves the state without a primary in that scenario;
            // here we close the loop so the user always ends up with an active DNA
            // once one is actually known.
            const hasPrimary = state.dnaReferences.some((r) => r.role === "primary");
            const dnaReferences = state.dnaReferences.map((r) => {
                if (r.id !== event.payload.referenceId)
                    return r;
                const role = !hasPrimary ? "primary" : r.role;
                return {
                    ...r,
                    bpm: event.payload.bpm,
                    key: event.payload.key,
                    mood: event.payload.mood,
                    role,
                };
            });
            return {
                ...state,
                dnaReferences,
                dnaReference: derivePrimary(dnaReferences),
                lastEventId: event.id,
            };
        }
        case "ReferenceRemoved": {
            // Event-sourced removal of a DNA reference (spec §10 #10): drop the
            // matching entry from `dnaReferences[]`. If the removed reference was the
            // active `primary`, promote the most recent surviving reference that
            // carries real DNA (not the placeholder seeded by an in-flight upload)
            // to `primary` so the user keeps a meaningful active DNA. With nothing
            // analyzed left, no fallback promotion happens — `dnaReference` simply
            // becomes `null` and a later `ReferenceAnalyzed` (see that case) will
            // promote the newly-analyzed reference. An unknown `referenceId` is a
            // no-op fail-soft, consistent with the rest of this file. The originating
            // `ReferenceUploaded` stays in the log — append-only never deletes.
            const idx = state.dnaReferences.findIndex((r) => r.id === event.payload.referenceId);
            if (idx === -1) {
                return { ...state, lastEventId: event.id };
            }
            const removed = state.dnaReferences[idx];
            const remaining = state.dnaReferences.filter((_, i) => i !== idx);
            let dnaReferences = remaining;
            if (removed.role === "primary" && remaining.length > 0) {
                // Most recent analyzed reference wins (newest non-placeholder). Sort
                // by `attachedAt` desc so we honor "newest" by timestamp, not the
                // possibly-mutated array index. `ReferenceAnalyzed` updates the
                // entry in place (same index, same attachedAt), so this stays stable.
                const analyzedByRecency = remaining
                    .filter((r) => r.bpm > 0 && r.key !== "?")
                    .sort((a, b) => (a.attachedAt < b.attachedAt ? 1 : a.attachedAt > b.attachedAt ? -1 : 0));
                const promoteId = analyzedByRecency[0]?.id;
                if (promoteId !== undefined) {
                    // Defensive demotion (#13): align with `DNAReferenceAttached` and
                    // `ReferenceUploaded` — never let two primaries coexist, even if a
                    // prior reducer state somehow already carried one in `remaining`.
                    dnaReferences = demoteExistingPrimaries(remaining).map((r) => r.id === promoteId ? { ...r, role: "primary" } : r);
                }
                // No analyzed reference left: do NOT promote a placeholder. Without a
                // primary, `derivePrimary` yields null; once the in-flight
                // `ReferenceAnalyzed` lands, the reducer's `ReferenceAnalyzed` case
                // promotes the newly-analyzed reference (see that case for the
                // promotion-on-arrival rule).
            }
            return {
                ...state,
                dnaReferences,
                dnaReference: derivePrimary(dnaReferences),
                lastEventId: event.id,
            };
        }
        case "SectionGenreShifted": {
            // `set` writes `section.genreShift`; `clear` returns it to `null` — a NEW
            // event, never a delete (invariant #1). An unknown sectionId is a no-op.
            const sections = mapSection(state, event.payload.sectionId, (s) => ({
                ...s,
                genreShift: event.payload.mode === "set"
                    ? { shiftKind: event.payload.shiftKind, descriptor: event.payload.descriptor }
                    : null,
            }));
            return { ...state, sections, lastEventId: event.id };
        }
        case "BandSonicTraitsOverridden": {
            // `custom` sets the override; `inherit` returns it to `null` — a NEW event,
            // never a delete (invariant #1). Re-inherits the band's derived traits.
            return {
                ...state,
                sonicTraitsOverride: event.payload.mode === "custom" ? event.payload.traits : null,
                lastEventId: event.id,
            };
        }
        default: {
            const _exhaustive = event;
            return _exhaustive;
        }
    }
}
export function reduceAll(state, events) {
    let acc = state;
    for (const e of events) {
        if (isMusical(e)) {
            acc = reduce(acc, e);
        }
    }
    return acc;
}
//# sourceMappingURL=reducer.js.map